import { useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { Tooltip } from "src/ui/shared/Tooltip";

import { selectGroups } from "@dashboard/groups/store/selectors";
import { getNestedGroupsWithIndentation } from "@dashboard/groups/utils";
import { useLicenses } from "@dashboard/licenses/api/helpers";
import { SectionDescription } from "@dashboard/shared/styles";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";
import { FormValues as ProvisioningFormValues } from "../ProvisioningPage";
import { requiredInternetConfigMessage } from "../utils";

import { hasInternetConfig } from "./InternetConfig/InternetConfig.utils";

export const DeviceRegistration = () => {
    const { value, onChange: onCollapseChange } = useCollapsiblePersistence(
        "provisioningPage.collapsedSections",
        "DeviceRegistration",
    );
    const parentForm = useForm();
    const { t } = useTranslation(["pages"]);

    const { values: parentFormValues } = useFormState<Partial<ProvisioningFormValues>>();
    const mdmClientEnabled = parentFormValues?.settings?.enableMdmClient;
    const noInternetConfig = !hasInternetConfig(parentFormValues?.settings?.internetConfig);
    const allGroups = useSelector(selectGroups);
    const groupOptions = getNestedGroupsWithIndentation(allGroups);
    const universalCodes = parentFormValues?.settings?.universalLicenses || [];
    const allLicenses = useLicenses();
    const allUniversalLicenses = allLicenses.filter((license) => !!license.universalGroupName);
    const universalLicenses = allUniversalLicenses.filter((license) => universalCodes?.includes(license.activationCode));
    const universallLicenseGroupIds = universalLicenses
        .filter((license) => license.universalGroupId !== null)
        .map((license) => license.universalGroupId);

    const onChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (typeof selectedOption?.value === "string") {
            return;
        }
        parentForm.change("registrationGroupId", selectedOption?.value);
        parentForm.change("settings.universalLicenses", []);
    };

    return (
        <CollapsibleProvider initialValue={value} onChange={onCollapseChange} disabled={noInternetConfig}>
            <CollapsibleSectionSummary>
                <CollapsibleSectionTitle>{t("pages:provisioningPage.section.deviceRegistration.title")}</CollapsibleSectionTitle>
            </CollapsibleSectionSummary>
            <CollapsibleSectionDetails error={noInternetConfig ? requiredInternetConfigMessage : undefined}>
                <SectionDescription>{t("pages:provisioningPage.section.deviceRegistration.description")}</SectionDescription>

                <Tooltip
                    childrenBoxStyles={{ display: "block" }}
                    title={mdmClientEnabled ? t("pages:provisioningPage.tooltip.deviceRegistration") : undefined}
                >
                    <SelectField
                        value={groupOptions.find(
                            (item) => item.value === (parentFormValues?.registrationGroupId || universallLicenseGroupIds[0]),
                        )}
                        onChange={onChange}
                        label={t("pages:provisioningPage.input.registrationGroup.caption")}
                        options={groupOptions}
                        fullWidth
                        disabled={mdmClientEnabled}
                        disableClearable={false}
                        disableSorting
                    />
                </Tooltip>
            </CollapsibleSectionDetails>
        </CollapsibleProvider>
    );
};
